import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { createShareLink, updateShareAccess, type ShareState } from '../api/chat-api';

// Query-key factory for a conversation's share state — namespaced under the
// shared `['chat','conversations']` root so a conversation-wide invalidation
// (rename/delete) also clears stale share data.
export function shareQueryKey(conversationId: string) {
  return ['chat', 'conversations', conversationId, 'share'] as const;
}

interface UseShareConversationResult {
  /** Latest share state, or null until the first create/toggle resolves. */
  share: ShareState | null;
  /** True while a create or access-toggle request is in flight. */
  isPending: boolean;
  /** Error from the last mutation, if any. */
  error: Error | null;
  /** Create the link, or rotate it when `regenerate` is true. */
  create: (opts?: { regenerate?: boolean }) => Promise<ShareState>;
  /** Convenience: rotate the slug (the old one stops resolving). */
  regenerate: () => Promise<ShareState>;
  /** Enable/disable public access without changing the slug. */
  setAccess: (isShared: boolean) => Promise<ShareState>;
}

/**
 * Drives the Share dialog's mutations for one conversation.
 *
 * The share state is held in the TanStack cache under `shareQueryKey(id)` and
 * written by every mutation's `onSuccess`, so create/regenerate/toggle all
 * stay in sync without a separate fetch (there is no GET for owner share
 * state — the dialog auto-creates on open).
 */
export function useShareConversation(conversationId: string | null): UseShareConversationResult {
  const queryClient = useQueryClient();
  const key = conversationId ? shareQueryKey(conversationId) : null;

  const writeCache = useCallback(
    (state: ShareState) => {
      if (key) queryClient.setQueryData<ShareState>(key, state);
    },
    [queryClient, key],
  );

  const createMutation = useMutation({
    mutationFn: ({ regenerate }: { regenerate?: boolean }) => {
      if (!conversationId) throw new Error('No conversation to share');
      return createShareLink(conversationId, { regenerate });
    },
    onSuccess: writeCache,
  });

  const accessMutation = useMutation({
    mutationFn: (isShared: boolean) => {
      if (!conversationId) throw new Error('No conversation to share');
      return updateShareAccess(conversationId, isShared);
    },
    onSuccess: writeCache,
  });

  const share = useMemo<ShareState | null>(
    () => (key ? (queryClient.getQueryData<ShareState>(key) ?? null) : null),
    // `createMutation.data` / `accessMutation.data` change whenever a mutation
    // resolves — re-derive the cached value off them so the dialog re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, queryClient, createMutation.data, accessMutation.data],
  );

  const create = useCallback(
    (opts?: { regenerate?: boolean }) =>
      createMutation.mutateAsync({ regenerate: opts?.regenerate ?? false }),
    [createMutation],
  );

  const regenerate = useCallback(() => create({ regenerate: true }), [create]);

  const setAccess = useCallback(
    (isShared: boolean) => accessMutation.mutateAsync(isShared),
    [accessMutation],
  );

  return {
    share,
    isPending: createMutation.isPending || accessMutation.isPending,
    error: (createMutation.error as Error | null) ?? (accessMutation.error as Error | null),
    create,
    regenerate,
    setAccess,
  };
}
